import { Box, Typography } from '@mui/material';
import { MouseEvent, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '../../../../../../../assets/delete-red.svg';
import EditIcon from '../../../../../../../assets/edit.svg';
import HistoryIcon from '../../../../../../../assets/history.svg';
import RenameIcon from '../../../../../../../assets/pen.svg';
import { CompleteOperationType, StartOperationType } from '../../../../../../../model/alerts.model';
import { UpdateRolePayload } from '../../../../../../../model/rolesAndPerms.model';
import { modalReducer } from '../../../../../../../reducers/modalReducer';
import RoutePaths from '../../../../../../../routes/RoutePaths';
import { AdminService } from '../../../../../../../service/admin.service';
import { useAppDispatch, useAppSelector } from '../../../../../../../store/hooks';
import { fetchRoles } from '../../../../../../../store/slices/rolesAndPermissionsSlice';
import zeplinColor from '../../../../../../../theme/Color';
import CustomDeleteModal from '../../../../../../shared/CustomDeleteModal/CustomDeleteModal';
import CustomInputModal from '../../../../../../shared/CustomInputModal/CustomInputModal';
import CustomPopOver from '../../../../../../shared/CustomPopOver/CustomPopOver';
import { RoleHistory } from '../RoleHistory';

export const RoleSettings = ({
  anchorElement,
  onClose,
  isRow,
  startOperation,
  completeOperation,
}: {
  anchorElement: SVGSVGElement | null;
  onClose: () => void;
  isRow?: boolean;
  startOperation: StartOperationType;
  completeOperation: CompleteOperationType;
}) => {
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    rename: false,
    history: false,
    delete: false,
  });
  const selectedRole = useAppSelector((state) => state.rolesAndPermissions.selectedRole)!;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onDeleteClick = async () => {
    try {
      startOperation('delete', 'Deleting role...');
      onClose();
      modalDispatch({ type: 'CLOSE', payload: 'delete' });
      await AdminService.deleteRole(selectedRole.roleId);
      await dispatch(fetchRoles());

      completeOperation('delete', 'Role deleted successfully');
    } catch (error) {
      completeOperation('delete', `Deletion Failed: ${error}`, true);
    }
  };

  const onRenameClick = async (newName: string) => {
    try {
      startOperation('rename', 'Updating name...');
      onClose();
      modalDispatch({ type: 'CLOSE', payload: 'rename' });
      const roleRes = await AdminService.getRoleDetails(selectedRole.roleId);
      const role = roleRes.role;
      const payload: UpdateRolePayload = {
        roleName: newName,
        salesforceRoleId: role.salesforceRoleId || '',
        description: role.description || '',
        permissions: role.isSuperAdmin
          ? []
          : (role.permissions || []).map((p: any) =>
              typeof p === 'string'
                ? p
                : `${p.module}.${(p.name || '').replace(/\s+/g, '')}.${p.action}`,
            ),
        isSuperAdmin: Boolean(role.isSuperAdmin),
        admin: Boolean(role.admin),
      };
      await AdminService.updateRole(selectedRole.roleId, payload);
      await dispatch(fetchRoles());

      completeOperation('rename', 'Updated name successfully');
    } catch (error) {
      completeOperation('rename', `Update Failed: ${error}`, true);
    }
  };

  const onHistoryClick = async () => {
    modalDispatch({ type: 'OPEN', payload: 'history' });
  };

  return (
    <CustomPopOver
      onClick={(e: MouseEvent<SVGSVGElement>) => {
        e.stopPropagation();
      }}
      setAnchorEl={onClose}
      anchorEl={anchorElement}
      id={'simple-popover'}
      sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px',
        }}
      >
        <Box
          sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
          onClick={() => {
            navigate(`${RoutePaths.Home}/admin/role/${selectedRole.roleId}`);
          }}
        >
          <img src={EditIcon} style={{ width: '18px', height: '18px' }} alt='rename' />
          <Typography variant='responsiveBody3'>Edit</Typography>
        </Box>
        <Box
          sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
          onClick={() => {
            modalDispatch({ type: 'OPEN', payload: 'rename' });
          }}
        >
          <img src={RenameIcon} style={{ width: '18px', height: '18px' }} alt='rename' />
          <Typography variant='responsiveBody3'>Rename Role</Typography>
        </Box>
        {!isRow && (
          <Box
            sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
            onClick={() => {
              onHistoryClick();
            }}
          >
            <img src={HistoryIcon} style={{ width: '18px', height: '18px' }} alt='role history' />
            <Typography variant='responsiveBody3'>Role History</Typography>
          </Box>
        )}
        {!selectedRole?.isSystem && (
          <Box
            sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
            onClick={() => {
              modalDispatch({ type: 'OPEN', payload: 'delete' });
            }}
          >
            <img src={DeleteIcon} style={{ width: '18px', height: '18px' }} alt='role history' />
            <Typography variant='responsiveBody3' sx={{ color: zeplinColor.Error }}>
              Delete
            </Typography>
          </Box>
        )}
      </Box>
      <CustomInputModal
        modalType={'rename'}
        title={'Rename Role'}
        label='Role name'
        isModalOpen={modalState.rename}
        modalDispatch={modalDispatch}
        handleAction={onRenameClick}
        inputValue={selectedRole ? selectedRole.roleName : ''}
      />
      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={onDeleteClick}
        title='Delete Role'
        description={`You’re about to delete the role “${selectedRole?.roleName}” This action will remove all permissions associated with it. Are you sure you want to delete?`}
      />
      <RoleHistory isModalOpen={modalState.history} modalDispatch={modalDispatch} />
    </CustomPopOver>
  );
};
